import { useRef } from 'react'

import { useAuthInfo } from '@sweetauth/core'

import { useBillingOrganization } from '@/hooks/billing/useBillingOrganization'
import { usePermissionCheck } from '@/hooks/permissions/usePermissionCheck'

import { UUID } from '@/types/Id'

export const useBillingRole = () => {
  const { orgHelper } = useAuthInfo()
  const { data: billingOrganization } = useBillingOrganization()

  const billingRole = orgHelper?.getOrg(
    billingOrganization?.id ?? '',
  )?.userAssignedRole

  return billingRole
}

export const useIsAtLeastAdmin = () => {
  return useIsAtLeastAdminState().isAtLeastAdmin
}

export const useIsAtLeastAdminState = () => {
  const { data: billingOrg, isLoading: isBillingOrgLoading } =
    useBillingOrganization()
  const permission = usePermissionCheck({
    resourceType: 'billing_organization',
    resourceId: billingOrg?.id as UUID,
    enabled: !!billingOrg,
    permissions: ['super_admin'],
  })

  // Hold the last resolved verdict through a re-check. Changing another member's role re-runs this
  // SpiceDB check, and its `data` is briefly undefined mid-flight — without retaining the prior value
  // the settings toggle (Admin Dashboard / All Organizations) flickers to the member view for a frame.
  // A genuine loss of admin still lands once the recheck resolves.
  const lastResolvedIsAdmin = useRef(false)
  if (permission.data !== undefined) {
    lastResolvedIsAdmin.current = permission.data.get('super_admin') === true
  }

  return {
    isAtLeastAdmin: lastResolvedIsAdmin.current,
    isLoading: isBillingOrgLoading || (!!billingOrg && permission.isLoading),
  }
}
